import * as React from "react";
import {
  Grid,
  Box,
  Stack,
  Typography,
  Button,
  alpha,
  IconButton,
  Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AppNavbar from "../Components/AppNavbar";
import Search from "../Components/Search";
import ColorModeIconDropdown from "../theme/ColorModeIconDropdown";
import Copyright from "../Components/Copyright";
import { ApiManager } from "../Services/Networking";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Edit, Trash } from "lucide-react";
import Layout from "../Components/Layout";

export default function Employee() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchText, setSearchText] = React.useState("");
  const [departments, setDepartments] = React.useState([]);
  const [employees, setEmployees] = React.useState(
    location.state?.updatedList || [],
  );
  const [loading, setLoading] = React.useState(!location.state?.updatedList);

  useEffect(() => {
    if (location.state?.updatedList) {
      setEmployees(location.state.updatedList);
      setLoading(false);
    } else {
      fetchData();
    }
    fetchDepartments();
  }, [location.state?.updatedList]);

  const fetchData = async () => {
    setLoading(true);
    const res = await ApiManager.getEmployees();
    if (res.status === "success") setEmployees(res.data);
    setLoading(false);
    fetchDepartments();
  };

  const fetchDepartments = async () => {
    setLoading(true);
    const deptRes = await ApiManager.getEmployeeDepartments();
    if (deptRes.status === "success") {
      setDepartments(deptRes.data);
    }
    setLoading(false);
  };

  const deleteEmp = async (id) => {
    if (window.confirm("Are you sure you want to delete this employee?")) {
      setLoading(true);
      const res = await ApiManager.deleteEmployee(id);
      if (res.status === "success") {
        setEmployees((prev) => prev.filter((e) => e.id !== id));
      }
      setLoading(false);
    }
  };

  const columns = [
    { field: "emp_id", headerName: "ID", width: 120 },
    { field: "emp_name", headerName: "Name", flex: 1, minWidth: 80 },
    {
      field: "dept_id",
      headerName: "Department",
      flex: 1,
      minWidth: 200,
      renderCell: (value) => {
        const hisDepartments = departments.filter(
          (x) => x.emp_id == value.row.emp_id,
        );
        if (hisDepartments.length > 0) {
          return hisDepartments.map((x) => (
            <Chip label={x.dept_name} size="small" variant="outlined" />
          ));
        } else {
          return "None Selected";
        }
      },
    },
    { field: "role", headerName: "Role", flex: 1, minWidth: 130 },
    { field: "mobile", headerName: "Mobile", flex: 1, minWidth: 150 },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 100,
      flex: 0.6,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center" height="100%">
          <IconButton
            onClick={() =>
              navigate("/employees/form", {
                state: { employee: params.row, fullList: employees },
              })
            }
          >
            <Edit size={18} />
          </IconButton>
          <IconButton onClick={() => deleteEmp(params.row.id)}>
            <Trash size={18} />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const filteredRows = employees.filter(
    (row) =>
      row.emp_name.toLowerCase().includes(searchText.toLowerCase()) ||
      row.emp_id.toLowerCase().includes(searchText.toLowerCase()),
  );

  return (
    <Layout name="EMPLOYEES">
      <Grid container spacing={2} columns={12}>
        <Grid size={{ xs: 12 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Search
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <span style={{ flex: 1 }}></span>
            <Button
              variant="contained"
              onClick={() =>
                navigate("/employees/form", {
                  state: { employee: null, fullList: employees },
                })
              }
            >
              Create New
            </Button>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12 }} style={{ minHeight: "580px" }}>
          <Box sx={{ height: 600 }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              rowHeight={70}
              loading={loading}
              getRowId={(row) => row.id}
              disableRowSelectionOnClick
            />
          </Box>
        </Grid>
      </Grid>
    </Layout>
  );
}
